import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { closeModal, showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { MODAL_KEY } from 'modules/auth/core/modal-key';

import type { Config, Exercises, Lessons } from '../type';

import {
  convertLevelStringToNumber,
  getExtFileName,
  hasEmptyValues,
  randomString,
  reorder,
  trimObject,
  trimValue,
} from 'helpers';
import { UploadFile } from 'helpers/upload-file';
import { useBoolean, useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { useWidthAndHeightFile } from 'hooks/widthAndHeightFile';
import { cloneDeep, debounce, includes, isEmpty, isEqual, isUndefined, orderBy } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';

import { defaultLessons } from '../constant';
import { ConfigType, ExerciseContentCategory } from '../enum';

export const useLessonsFormController = () => {
  const navigationTo = useNavigate();

  const params = useParams();

  const { id, institutionId } = params;

  const userProfile = useAuthStore(userProfileSelector);

  const isInstitutionDetail = location.pathname.includes(`${PATH.INSTITUTIONS}`);

  const isLessonAdd = location.pathname.includes(`${PATH.ADD_LESSONS}`);

  const [selectedExercises, setSelectedExercises] = useState<Record<string, string>>({});

  const [lessonDetail, setLessonDetail] = useState<Lessons>();

  const [configs, setConfigs] = useState<Config[]>([]);

  const [exercisesData, setExercisesData] = useState<Exercises[]>([]);

  const searchValue = useString('');

  const thumbnailUrl = useString('');

  const activeLevel = useString('level1');

  const isEditExercises = useBoolean(true);

  const [selectedData, setSelectedData] = useState<Exercises[]>([]);

  const [deleteSelected, setDeleteSelected] = useState<Exercises[]>([]);

  const [exercisesDataCommon, setExercisesDataCommon] = useState<Record<string, Exercises[]>>();

  const [page, setPage] = useState<number>(1);

  const [thumbnail, setThumbnail] = useState<File | null>(null);

  const totalPage = useNumber(0);

  const hasMore = useBoolean(false);

  const isLoading = useBoolean(false);

  const isGenerating = useBoolean(false);

  const { handleGetWidthAndHeightThumbnail, thumbnailHeight, thumbnailWidth } =
    useWidthAndHeightFile();

  useEffect(() => {
    getConfigs();
  }, []);

  useEffect(() => {
    if (page < 1) {
      return;
    }

    getListExercises();
  }, [page]);

  useEffect(() => {
    if (isLessonAdd) {
      return;
    }

    if (id) {
      getLessonDetail();
    }
  }, [id]);

  useEffect(() => {
    if (
      !id ||
      (isInstitutionDetail && isLessonAdd)
      // || (id && values.type !== lessonDetail?.type.id)
    ) {
      getExercisesDataDIY();
    }
  }, [lessonDetail, isGenerating.value]);

  useEffect(() => {
    if ((id && !isInstitutionDetail) || (isInstitutionDetail && !isLessonAdd))
      isEditExercises.setValue(false);
  }, []);

  const types = useMemo(() => {
    if (isEmpty(configs)) {
      return [];
    }

    return configs
      .filter((el) => el.type === ConfigType.EXERCISE_CONTENT_TYPE)
      .sort((a, b) => {
        if (a.key === 'supervised' && b.key === 'diy') {
          return -1;
        }

        if (a.key === 'diy' && b.key === 'supervised') {
          return 1;
        }

        return 0;
      });
  }, [configs]);

  const getListExercises = async (search: string = '') => {
    const result: {
      items: Exercises[];
      total: number;
    } = await apiCall({
      url: '/exercises',
      method: 'GET',
      data: {
        page: page.toString(),
        limit: 10,
        institutionId: isInstitutionDetail ? institutionId : userProfile?.institution?.id,
        search: search,
      },
    });

    hasMore.setValue(result?.items?.length === 10);

    setTimeout(() => {
      isLoading.setValue(false);
    }, 1000);

    const newItems = result?.items ?? [];

    if (page < 1) return;

    if (page === 1) {
      setExercisesData(newItems);
      totalPage.setValue(result.total);

      return;
    }

    setExercisesData((prev) => [...prev, ...newItems]);
  };

  const getConfigs = async () => {
    const result: Config[] = await apiCall({
      url: '/configs',
      method: 'GET',
    });

    if (result) {
      const typesConfig = result.filter((el) => el.type === ConfigType.EXERCISE_CONTENT_TYPE);
      const typeIdSupervised = typesConfig.find((el) => el.key === 'supervised')?.id;

      (!id || (isInstitutionDetail && isLessonAdd)) && setFieldValue('type', typeIdSupervised);
      setConfigs(result ?? []);
    }
  };

  const getLessonDetail = async () => {
    const result: Lessons = await apiCall({
      url: `/lessons/${id}`,
      method: 'GET',
      isLoading: true,
    });

    if (result?.id) {
      setValues(result as any);
      setFieldValue('type', result.type.id);
      setLessonDetail({
        ...result,
        name: result.name,
        type: result.type,
        totalExercises: result.totalExercises,
        exercises: orderBy(result.exercises, 'index'),
      } as any);
      thumbnailUrl.setValue(result.thumbnail ?? '');

      if (id) {
        const newResult = cloneDeep(result) as any;
        const { level1, level2, level3 } = newResult;

        setExercisesDataCommon({
          level1: orderBy(level1, 'index'),
          level2: orderBy(level2, 'index'),
          level3: orderBy(level3, 'index'),
        });
      }

      // if (result.type.key === 'supervised') {
      //   setSelectedData(orderBy(result.exercises, 'index'));
      // }
    }
  };

  const onChangeSelectedExercises = (exercises: string) => () => {
    const newSelected = cloneDeep(selectedExercises);

    if (!isUndefined(newSelected[exercises])) {
      delete newSelected[exercises];
    } else {
      newSelected[exercises] = exercises;
    }

    setSelectedExercises(newSelected);
  };

  // const newSelectedExerciseData = selectedData.map(
  //   (el) =>
  //     ({
  //       ...el,
  //       reps: Number(el.reps),
  //       sets: Number(el.sets),
  //       restTime: Number(el.restTime),
  //       duration: Number(el.duration),
  //     }) as Exercises,
  // );

  const newSelectedExerciseData =
    exercisesDataCommon &&
    Object.values(exercisesDataCommon)
      .flat()
      .map(
        (el) =>
          ({
            index: el.index,
            lessonExerciseId: el.lessonExerciseId,
            duration: Number(el.duration),
            id: el.id,
            level: el.level,
            lowerBody: el.lowerBody,
            name: el.name,
            reps: Number(el.reps),
            restTime: Number(el.restTime),
            sets: Number(el.sets),
            thumbnail: el.thumbnail,
            upperBody: el.upperBody,
            category: el.category,
            lessonLevel: el.lessonLevel,
          }) as Exercises,
      );

  const handleUploadVideoAndThumbnail = async () => {
    let urlThumbnail = thumbnailUrl.value;

    let nameFileImage = '';

    try {
      if (thumbnail) {
        const thumbnailExt = getExtFileName(thumbnail.name);

        nameFileImage = `thumbnail-exercise-${randomString(4)}-${Date.now()}${thumbnailExt}`;

        urlThumbnail = await UploadFile(thumbnail, nameFileImage);
      }

      return {
        urlThumbnail,
        nameFileImage,
      };
    } catch (error) {
      console.log(error);

      return null;
    }
  };

  const formik = useFormik({
    initialValues: defaultLessons,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      const resultUpload: {
        urlThumbnail: string;
        nameFileImage?: string;
      } | null = await handleUploadVideoAndThumbnail();

      if (!resultUpload) {
        return;
      }

      const bodyRequest = {
        ...trimObject({
          name: values.name,
          type: values.type,
          thumbnail: resultUpload.nameFileImage,
          institutionId: userProfile?.institution?.id,
          exercises: newSelectedExerciseData?.map((el: any, index) => ({
            index,
            sets: el.sets,
            reps: el.reps,
            duration: el.duration,
            restTime: el.restTime,
            lessonExerciseId: el.lessonExerciseId,
            exerciseId: el.id,
            lessonLevel: el.lessonLevel,
          })),
          removeLessonExercises: deleteSelected.map((el) => el.lessonExerciseId),
        }),
      };

      if (resultUpload.urlThumbnail && thumbnailHeight.value && thumbnailWidth.value) {
        bodyRequest.thumbnailHeight = thumbnailHeight.value;
        bodyRequest.thumbnailWidth = thumbnailWidth.value;
      }

      if (
        !isLessonAdd &&
        !resultUpload.urlThumbnail &&
        String(lessonDetail?.thumbnail ?? '') !== String(thumbnailUrl.value ?? '')
      ) {
        bodyRequest.isRemoveThumbnail = true;
      }

      if (isLessonAdd && isInstitutionDetail) {
        bodyRequest.institutionId = institutionId;
      }

      if (!resultUpload.nameFileImage) {
        delete bodyRequest.thumbnail;
      }

      const result: Lessons = await apiCall({
        url: !isLessonAdd ? `/lessons/${id}` : '/lessons',
        method: !isLessonAdd ? 'PATCH' : 'POST',
        data: bodyRequest,
        isLoading: true,
        showError: true,
      });

      if (!result?.id) {
        return;
      }

      if (!isLessonAdd) {
        getLessonDetail();
        setSelectedData([]);
        showToastMessage('success', SUCCESS_MESSAGE.UPDATE_LESSON);

        return;
      }

      showToastMessage('success', SUCCESS_MESSAGE.ADD_LESSON);
      formik.setValues(defaultLessons);
      setSelectedExercises({});

      navigationTo(-1);
    },
  });

  const { values, isSubmitting, isValid, handleSubmit, handleChange, setValues, setFieldValue } =
    formik;

  const getExercisesDataDIY = async () => {
    const result: Record<string, Exercises[]> = await apiCall({
      url: '/exercises/level',
      method: 'GET',
      data: {
        institutionId: isInstitutionDetail && isLessonAdd ? id : userProfile?.institution?.id,
      },
    });

    if (Object.keys(result).length !== 0) {
      const formatResult = isGenerating.value ? result : initExercisesData(result);

      setExercisesDataCommon(formatResult);
    }
  };

  const initExercisesData = (result: Record<string, Exercises[]>) => {
    const filterAndOrderExercises = (exercises: Exercises[]) =>
      orderBy(exercises, 'index').filter(
        (ele) =>
          ele.category.key === ExerciseContentCategory.WARMUP ||
          ele.category.key === ExerciseContentCategory.COOLDOWN,
      );

    return Object.fromEntries(
      Object.entries(result).map(([level, exercises]) => [
        level,
        filterAndOrderExercises(exercises),
      ]),
    );
  };

  const isEmptyOnceLevel = Object.values(exercisesDataCommon ?? {}).some((exercises) => {
    const nonWarmupCooldownExercises = exercises.filter(
      (exercise) =>
        exercise.category.key !== ExerciseContentCategory.WARMUP &&
        exercise.category.key !== ExerciseContentCategory.COOLDOWN,
    );

    return nonWarmupCooldownExercises.length === 0;
  });

  const isEmptyValueOfExerciseDIY =
    exercisesDataCommon &&
    Object.values(exercisesDataCommon).some((e) =>
      e.some((el) => !el.sets || !el.reps || !el.restTime || !el.duration),
    );

  const disabled =
    hasEmptyValues({ name: values.name, type: values.type }) ||
    isSubmitting ||
    !isValid ||
    isEmptyOnceLevel ||
    (isEmptyValueOfExerciseDIY as boolean);

  const onChangeType = (newItem: Config) => () => {
    if (newItem) {
      setFieldValue('type', newItem.id);
    }
  };

  const onCloseModal = () => {
    closeModal(MODAL_KEY.ADD_LESSONS);
    formik.setValues(defaultLessons);
  };

  const onCancel = () => {
    if (id) {
      setFieldValue('name', lessonDetail?.name);
      thumbnailUrl.setValue(lessonDetail?.thumbnail as string);
      setExercisesDataCommon({
        level1: orderBy((lessonDetail as any).level1, 'index'),
        level2: orderBy((lessonDetail as any).level2, 'index'),
        level3: orderBy((lessonDetail as any).level3, 'index'),
      });
      setFieldValue('type', lessonDetail?.type.id);

      return;
    }

    if (!isLessonAdd && id) {
      setValues(lessonDetail as any);
      thumbnailUrl.setValue(lessonDetail?.thumbnail as string);
      setSelectedData(lessonDetail?.exercises as Exercises[]);

      return;
    }

    formik.setValues(defaultLessons);
    navigationTo(-1);
  };

  const onChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      getListExercises(trimValue(newSearch));
    }, 500),
    [],
  );

  const handleAddExercisesToLesson = () => {
    const newSelectedData = exercisesData.filter((el) =>
      Object.keys(selectedExercises).includes(el.id),
    );

    if (exercisesDataCommon) {
      const findIndexCoolDown = exercisesDataCommon?.[activeLevel.value].findIndex(
        (ele) => ele.category.key === ExerciseContentCategory.COOLDOWN,
      );

      const cloneNewSelectedData = cloneDeep(newSelectedData);

      cloneNewSelectedData.forEach((el) => {
        el.lessonLevel = convertLevelStringToNumber(activeLevel.value);
      });

      if (findIndexCoolDown !== -1) {
        const cloneExercisesDataDIY = cloneDeep(exercisesDataCommon);

        const exercisesCoolDownIndex = cloneExercisesDataDIY[activeLevel.value].findIndex((ele) =>
          includes([ExerciseContentCategory.COOLDOWN], ele.category.key),
        );

        const newArray = [...cloneExercisesDataDIY[activeLevel.value]];

        newArray.splice(exercisesCoolDownIndex, 0, ...cloneNewSelectedData);

        setExercisesDataCommon({
          ...cloneExercisesDataDIY,
          [activeLevel.value]: newArray,
        });
      }
    }

    setSelectedData([...selectedData, ...newSelectedData]);

    setSelectedExercises({});
  };

  const handleDeleteExerciseInLesson = (index: number) => {
    const newSelectedData = cloneDeep(selectedData);

    setDeleteSelected([...deleteSelected, newSelectedData[index]]);

    newSelectedData.splice(index, 1);

    setSelectedData(newSelectedData);
  };

  const handleDeleteExerciseDIYInLesson = (index: number) => {
    const newExercisesDataDIY = { ...exercisesDataCommon };

    setDeleteSelected([...deleteSelected, newExercisesDataDIY[activeLevel.value][index]]);

    newExercisesDataDIY[activeLevel.value].splice(index, 1);

    setExercisesDataCommon(newExercisesDataDIY);
  };

  const onChangeValueSelectedExercises = (index: number, value: Record<string, string>) => {
    const newSelectedData = [...selectedData];

    newSelectedData[index] = { ...newSelectedData[index], ...value };

    setSelectedData(newSelectedData);
  };

  const onChangeValueSelectedExercisesDIY = (index: number, value: Record<string, string>) => {
    const newExercisesDataDIY = { ...exercisesDataCommon };

    newExercisesDataDIY[activeLevel.value][index] = {
      ...newExercisesDataDIY[activeLevel.value][index],
      ...value,
    };

    setExercisesDataCommon(newExercisesDataDIY);
  };

  const handleDeleteLesson = async () => {
    await apiCall({
      url: `/lessons/${id}`,
      method: 'DELETE',
      isLoading: true,
      showError: true,
    });
    closeModal(MODAL_KEY.DELETE_LESSON);
    showToastMessage('success', SUCCESS_MESSAGE.DELETE_LESSON);
    navigationTo(-1);
  };

  const exercisesDataInit = () => {
    const { level1, level2, level3 } = lessonDetail as any;

    return [...orderBy(level1, 'index'), ...orderBy(level2, 'index'), ...orderBy(level3, 'index')];
  };

  const disabledButton = useMemo(() => {
    return (
      (String(lessonDetail?.name) === values.name &&
        Number(lessonDetail?.type.id) === values.type &&
        String(lessonDetail?.thumbnail ?? '') === String(thumbnailUrl.value ?? '') &&
        isEqual(lessonDetail && exercisesDataInit(), newSelectedExerciseData)) ||
      isEmptyOnceLevel ||
      isSubmitting ||
      hasEmptyValues({ name: values.name, type: values.type })
    );
  }, [values, lessonDetail, exercisesDataCommon, newSelectedExerciseData]);

  const goBack = () => {
    navigationTo(-1);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const itemsSelectedData = reorder(selectedData, result.source.index, result.destination.index);

    const itemsExercisesData = reorder(
      exercisesDataCommon ? exercisesDataCommon[activeLevel.value] : [],
      result.source.index,
      result.destination.index,
    );

    const findIndexCoolDown = exercisesDataCommon?.[activeLevel.value].findIndex(
      (ele) => ele.category.key === ExerciseContentCategory.COOLDOWN,
    );
    const findIndexWarmUp = exercisesDataCommon?.[activeLevel.value].findIndex(
      (ele) => ele.category.key === ExerciseContentCategory.WARMUP,
    );
    const findIndexEndingCheer = exercisesDataCommon?.[activeLevel.value].findIndex(
      (ele) => ele.category.key === ExerciseContentCategory.ENDINGCHEERUP,
    );

    const endingCheer = exercisesDataCommon?.[activeLevel.value].find(
      (el) =>
        el.category.key === ExerciseContentCategory.ENDINGCHEERUP &&
        el.id + result.source.index === result.draggableId,
    );

    if (
      (result.destination.index === findIndexCoolDown && !endingCheer) ||
      result.destination.index === findIndexWarmUp ||
      result.destination.index === findIndexEndingCheer
    ) {
      return;
    }

    setExercisesDataCommon({
      ...exercisesDataCommon,
      [activeLevel.value]: itemsExercisesData,
    });

    setSelectedData(itemsSelectedData);
  };

  const onTabClickLevel = (level: string) => {
    activeLevel.setValue(`level${level}`);
  };

  const handleChangeEditExercises = () => {
    isEditExercises.setValue(!isEditExercises.value);
  };

  const handleCalculateTotalUpperAndLower = () => {
    const dataExercisesActive =
      exercisesDataCommon?.[activeLevel.value]?.filter(
        (el) =>
          ![
            ExerciseContentCategory.WARMUP,
            ExerciseContentCategory.COOLDOWN,
            ExerciseContentCategory.MIDPOINTCHEERUP,
            ExerciseContentCategory.ENDINGCHEERUP,
          ].includes(el.category.key as ExerciseContentCategory),
      ) ?? [];

    const totalExercises = dataExercisesActive.length;

    if (totalExercises === 0) {
      return { averageUpper: 0, averageLower: 0 };
    }

    const { totalUpper, totalLower } = dataExercisesActive.reduce(
      (acc, exercise) => ({
        totalUpper: acc.totalUpper + (Number(exercise.upperBody) ?? 0),
        totalLower: acc.totalLower + (Number(exercise.lowerBody) ?? 0),
      }),
      { totalUpper: 0, totalLower: 0 },
    );

    const roundToOneDecimal = (value: number) => {
      const rounded = Math.round(value * 10) / 10;

      return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
    };

    const averageUpper = roundToOneDecimal(totalUpper / totalExercises);
    const averageLower = roundToOneDecimal(totalLower / totalExercises);

    return { averageUpper, averageLower };
  };

  const handleLoadMoreExercise = () => {
    if (!hasMore.value || isLoading.value) {
      return;
    }

    isLoading.setValue(true);

    setTimeout(() => {
      setPage((prev) => prev + 1);
    }, 1200);
  };

  const handleGenerate = () => {
    isGenerating.setValue(true);
    isEditExercises.setValue(false);
  };

  const handleRemoveThumbnail = () => {
    setThumbnail(null);

    if (lessonDetail?.thumbnail?.includes('thumbnail-default')) {
      thumbnailUrl.setValue(lessonDetail?.thumbnail);
    } else {
      thumbnailUrl.setValue('');
    }
  };

  const onChangeThumbnail = (files: File[]) => {
    const file = files[0];

    if (file && file.type.includes('image')) {
      setThumbnail(file);
      thumbnailUrl.setValue(URL.createObjectURL(file));
    } else {
      showToastMessage('error', 'Error format image');
    }
  };

  return {
    selectedExercises,
    onChangeSelectedExercises,
    onCloseModal,
    handleChange,
    handleSubmit,
    disabled,
    values,
    lessonId: isLessonAdd ? '' : id,
    disabledButton,
    types,
    onChangeType,
    onCancel,
    exercisesData,
    searchValue,
    onChangeSearchValue,
    selectedData,
    handleAddExercisesToLesson,
    handleDeleteExerciseInLesson,
    onChangeValueSelectedExercises,
    handleDeleteLesson,
    lessonDetail,
    goBack,
    onDragEnd,
    onTabClickLevel,
    activeLevel,
    exercisesDataCommon,
    onChangeValueSelectedExercisesDIY,
    handleDeleteExerciseDIYInLesson,
    isEditExercises,
    handleChangeEditExercises,
    handleCalculateTotalUpperAndLower,
    hasMore,
    handleLoadMoreExercise,
    isGenerating,
    handleGenerate,
    thumbnailUrl,
    handleGetWidthAndHeightThumbnail,
    handleRemoveThumbnail,
    onChangeThumbnail,
  };
};
